import { useCallback, useMemo } from "react";
import { AudienceDefinitions } from "../../../../../dtos/AudienceDefinitions";
import { GrantCommentAudienceType } from "../../../../../dtos/GrantCommentAudienceType";
import { GrantDto } from "../../../../../dtos/GrantDto";
import { Modules } from "../../../../../dtos/Modules";
import { NotificationTrigger } from "../../../../../dtos/NotificationTrigger";
import { PermissionType } from "../../../../../dtos/PermissionType";
import { StepDto } from "../../../../../dtos/StepDto";
import { UserRole } from "../../../../../dtos/UserRole";
import { PhaseDto } from "../../../../../dtos/PhaseDto";
import { useAppContext } from "../../../AppContext";
import { checkStepPermission, getEffectiveUserRole } from "../../../Utils";
import { useTaskListContext } from "../../context/TaskListContext";
import { defaultAudiences, roleLabels } from "../../config/audienceConfig";

// Gets the audience configuration from the phase, falling back to defaults
function getAudienceConfig(phase: PhaseDto | null): AudienceDefinitions {
    return phase?.defaultPhase?.settings?.audiences ?? defaultAudiences;
}

// Determines the user's audience based on their role and the phase's audience configuration
// Returns the audience type, display text and the roles in it, or null if the user's role isn't in any audience
function getUserAudience(
    userRole: UserRole,
    audiences: AudienceDefinitions
): { type: GrantCommentAudienceType; text: string; roles: UserRole[] } | null {
    if (audiences.grantee?.includes(userRole)) {
        return { type: GrantCommentAudienceType.GranteeOnly, text: "Institution Only", roles: audiences.grantee };
    }
    if (audiences.nih?.includes(userRole)) {
        return { type: GrantCommentAudienceType.NIHOnly, text: "NIH Only", roles: audiences.nih };
    }
    return null;
}

type IUseSubTaskCommentsControllerProps = {
    grantId: string;
    grant?: GrantDto | null;
    step: StepDto;
};

export function useSubTaskCommentsController({ grantId, grant, step }: IUseSubTaskCommentsControllerProps) {
    const app = useAppContext();
    const taskCtx = useTaskListContext();

    const { audienceOptions, defaultAudience, audienceRoleNames } = useMemo(() => {
        const selectedPhase = taskCtx.getSelectedPhase();
        const audiences = getAudienceConfig(selectedPhase);

        // Resolve effective role - for external users, this converts EXTERNAL_USER to their
        // grant-specific role (e.g., EXTERNAL_PROJECT_MANAGER) based on grant configuration
        // Super Users use their SU role which is part of the NIH audience
        const effectiveRole = getEffectiveUserRole(app, grantId);

        if (effectiveRole) {
            const userAudience = getUserAudience(effectiveRole, audiences);
            if (userAudience) {
                return {
                    audienceOptions: [{ text: userAudience.text, value: userAudience.type }],
                    defaultAudience: userAudience.type,
                    audienceRoleNames: userAudience.roles.map(role => roleLabels[role] ?? UserRole[role]),
                };
            }
        }

        // User's role not in any configured audience - return empty options with All default
        return {
            audienceOptions: [] as Array<{ text: string; value: GrantCommentAudienceType }>,
            defaultAudience: GrantCommentAudienceType.All,
            audienceRoleNames: [] as string[],
        };
    }, [taskCtx, app, grantId]);

    // Use config-based permission check for visibility and editing (with phase fallback)
    // Pass grantId for resolving external user roles
    const hasCommentPermission = useMemo(() => {
        const selectedPhase = taskCtx.getSelectedPhase();
        return checkStepPermission(step, app, PermissionType.Comment, true, selectedPhase, grantId);
    }, [step, app, taskCtx, grantId, grant]);

    const isVisible = useMemo(() => {
        // Check View permission from config
        const selectedPhase = taskCtx.getSelectedPhase();
        if (!checkStepPermission(step, app, PermissionType.View, true, selectedPhase, grantId)) {
            return false;
        }

        // Show comments section if user has comment permission
        return hasCommentPermission;
    }, [step, app, taskCtx, grantId, grant, hasCommentPermission]);

    // Comments stay open on every cycle, so this is the permission alone (config handles status
    // restrictions).
    const isReadOnly = useMemo(() => !hasCommentPermission, [hasCommentPermission]);

    const isDisabled = useMemo(() => {
        return step?.status == null;
    }, [step?.status]);

    // Mirror backend fallback (NotificationService.GetRecipientsForStepTriggerAsync):
    // DefaultStep settings win; otherwise fall back to phase DefaultStepSettings.
    const notifyVisible = useMemo(() => {
        const stepCfg = step?.defaultStep?.settings?.notifications?.triggerConfigs?.find(
            (tc) => tc.trigger === NotificationTrigger.OnComment
        );
        if (stepCfg) return stepCfg.enabled;

        const phase = taskCtx.getSelectedPhase();
        const phaseCfg = phase?.defaultPhase?.settings?.defaultStepSettings?.notifications?.triggerConfigs?.find(
            (tc) => tc.trigger === NotificationTrigger.OnComment
        );
        return phaseCfg?.enabled ?? false;
    }, [step, taskCtx]);

    const onCommentAdded = useCallback(async () => {
        try {
            taskCtx.setSelectedStep(step);
            taskCtx.refreshComments();
            await taskCtx.refreshSteps(step);
        } catch (error) {
            if (error instanceof DOMException && error.name === "AbortError") return;
            if (error instanceof Error && error.message === "canceled") return;
            // Log other errors but don't throw - refresh failures are non-critical
            console.warn("Error refreshing after comment added:", error);
        }
    }, [taskCtx, step]);

    const module = taskCtx.getSelectedPhase()?.defaultPhase?.moduleTypeId as Modules;

    return {
        isVisible,
        isReadOnly,
        isDisabled,
        audienceOptions,
        defaultAudience,
        audienceRoleNames,
        displayType: "button" as const,
        notifyDefault: true as const,
        notifyVisible,
        module,
        grantId,
        stepId: step.id,
        stepName: step.name,
        onCommentAdded,
    };
}
